import { CliError, EXIT_USAGE } from "../../../common.js";
import type {
  ActionV4,
  ActionIdV4,
  ActorIdV4,
  ActorV4,
  EpisodeIdV4,
  EpisodeV4,
  LocationIdV4,
  LocationV4,
  PropIdV4,
  PropV4,
  SceneIdV4,
  SceneV4,
  ScriptV4,
  StateIdV4,
  StateV4,
} from "./types.js";
import { isV4Id } from "./ids.js";

export type V4Address =
  | { kind: "episode"; id: EpisodeIdV4 }
  | { kind: "scene"; id: SceneIdV4 }
  | { kind: "action"; id: ActionIdV4 }
  | { kind: "asset"; assetKind: "actor"; id: ActorIdV4 }
  | { kind: "asset"; assetKind: "location"; id: LocationIdV4 }
  | { kind: "asset"; assetKind: "prop"; id: PropIdV4 }
  | { kind: "state"; assetKind: "actor"; assetId: ActorIdV4; stateId: StateIdV4 }
  | { kind: "state"; assetKind: "location"; assetId: LocationIdV4; stateId: StateIdV4 }
  | { kind: "state"; assetKind: "prop"; assetId: PropIdV4; stateId: StateIdV4 };

function invalidAddress(value: string): never {
  throw new CliError("Use a bare typed v4 ID or asset/state address.", {
          exitCode: EXIT_USAGE,
          errorCode: "V4_ADDRESS_INVALID",
          data: { expected: ["ep… | scn… | ac… | act… | loc… | prp… | asset_id/state_id"], actual: [value || "<empty>"] },
        });
}

export function parseV4Address(raw: unknown): V4Address {
  const value = typeof raw === "string" ? raw.trim() : "";
  const slash = value.indexOf("/");
  if (slash >= 0) {
    if (slash !== value.lastIndexOf("/")) invalidAddress(value);
    const assetId = value.slice(0, slash);
    const stateId = value.slice(slash + 1);
    if (stateId !== "default" && !isV4Id("state", stateId)) invalidAddress(value);
    if (isV4Id("actor", assetId)) return { kind: "state", assetKind: "actor", assetId, stateId };
    if (isV4Id("location", assetId)) return { kind: "state", assetKind: "location", assetId, stateId };
    if (isV4Id("prop", assetId)) return { kind: "state", assetKind: "prop", assetId, stateId };
    return invalidAddress(value);
  }
  if (isV4Id("episode", value)) return { kind: "episode", id: value };
  if (isV4Id("scene", value)) return { kind: "scene", id: value };
  if (isV4Id("action", value)) return { kind: "action", id: value };
  if (isV4Id("actor", value)) return { kind: "asset", assetKind: "actor", id: value };
  if (isV4Id("location", value)) return { kind: "asset", assetKind: "location", id: value };
  if (isV4Id("prop", value)) return { kind: "asset", assetKind: "prop", id: value };
  return invalidAddress(value);
}

export interface EpisodeLocationV4 {
  episode: EpisodeV4;
  episodeIndex: number;
}

export interface SceneLocationV4 extends EpisodeLocationV4 {
  scene: SceneV4;
  sceneIndex: number;
  globalSceneIndex: number;
}

export interface ActionLocationV4 extends SceneLocationV4 {
  action: ActionV4;
  actionIndex: number;
  globalActionIndex: number;
}

export function listEpisodeLocationsV4(script: ScriptV4): EpisodeLocationV4[] {
  return script.episodes.map((episode, episodeIndex) => ({ episode, episodeIndex }));
}

export function listSceneLocationsV4(script: ScriptV4): SceneLocationV4[] {
  const rows: SceneLocationV4[] = [];
  let globalSceneIndex = 0;
  script.episodes.forEach((episode, episodeIndex) => {
    episode.scenes.forEach((scene, sceneIndex) => {
      rows.push({ episode, episodeIndex, scene, sceneIndex, globalSceneIndex });
      globalSceneIndex++;
    });
  });
  return rows;
}

export function listActionLocationsV4(script: ScriptV4): ActionLocationV4[] {
  const rows: ActionLocationV4[] = [];
  let globalSceneIndex = 0;
  let globalActionIndex = 0;
  script.episodes.forEach((episode, episodeIndex) => {
    episode.scenes.forEach((scene, sceneIndex) => {
      scene.actions.forEach((action, actionIndex) => {
        rows.push({
          episode,
          episodeIndex,
          scene,
          sceneIndex,
          globalSceneIndex,
          action,
          actionIndex,
          globalActionIndex,
        });
        globalActionIndex++;
      });
      globalSceneIndex++;
    });
  });
  return rows;
}

export function findEpisodeV4(script: ScriptV4, id: string): EpisodeLocationV4 | null {
  return listEpisodeLocationsV4(script).find((row) => row.episode.episode_id === id) ?? null;
}

export function findSceneV4(script: ScriptV4, id: string): SceneLocationV4 | null {
  return listSceneLocationsV4(script).find((row) => row.scene.scene_id === id) ?? null;
}

export function findActionV4(script: ScriptV4, id: string): ActionLocationV4 | null {
  return listActionLocationsV4(script).find((row) => row.action.action_id === id) ?? null;
}

export function findAssetV4(
  script: ScriptV4,
  kind: "actor" | "location" | "prop",
  id: string,
): ActorV4 | LocationV4 | PropV4 | null {
  if (kind === "actor") return script.actors.find((asset) => asset.actor_id === id) ?? null;
  if (kind === "location") return script.locations.find((asset) => asset.location_id === id) ?? null;
  return script.props.find((asset) => asset.prop_id === id) ?? null;
}

export function findStateV4(
  script: ScriptV4,
  kind: "actor" | "location" | "prop",
  assetId: string,
  stateId: string,
): StateV4 | null {
  return findAssetV4(script, kind, assetId)?.states.find((state) => state.state_id === stateId) ?? null;
}
